import { use, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import GatewayOverviewRecord from 'features/gateway/domain/models/gateway-overview-record';
import { useReadGateway } from 'features/gateway/domain/queries/gateway-queries';
import useGatewayDetails, { GatewayDetailsHook } from 'features/gateway/hooks/gateway-details-hook';
import useGatewayFilters, { IGatewayFiltersHook } from 'features/gateway/hooks/gateway-filters-hook';

type GatewayDetailAction = 'viewing' | 'editing' | 'refreshing';

export interface IGatewaysHook {
  gatewayDetailsHook: GatewayDetailsHook;
  gatewayFilterHook: IGatewayFiltersHook;

  openDetailsInCreationMode: () => void;
  openDetailsInViewingModeAndRefresh: (gateway: GatewayOverviewRecord) => void;
  openDetailsInEditingModeAndRefresh: (gateway: GatewayOverviewRecord) => void;
  deleteGateway: (gateway: GatewayOverviewRecord) => void;
  refresh: () => void;
}

const useGateways = (): IGatewaysHook => {
  const [gatewayIdToOpen, setGatewayIdToOpen] = useState<string>();
  const [gatewayDetailAction, setGatewayDetailAction] = useState<GatewayDetailAction>();

  const gatewayFilterHook = useGatewayFilters();
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const refresh = () => {
    gatewayFilterHook.triggerReadGateways();
    gatewayFilterHook.loadMonitorFilters();
  };

  const refreshGatewayDetails = (gatewayId: string) => {
    setGatewayDetailAction('refreshing');
    setGatewayIdToOpen(gatewayId);
  };

  const gatewayDetailsHook = useGatewayDetails(refresh, refreshGatewayDetails);
  const location = useLocation();
  const {
    data: gatewayToOpen,
    error: gatewayToOpenError,
    isError: gatewayToOpenIsError,
    isFetching: gatewayToOpenIsFetching,
  } = useReadGateway(gatewayIdToOpen);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const gatewayId = queryParams.get('id');

    if (gatewayId) {
      setGatewayDetailsToOpen(gatewayId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (gatewayToOpen && gatewayDetailAction && !gatewayToOpenIsFetching && gatewayToOpen.id === gatewayIdToOpen) {
      if (gatewayDetailAction === 'viewing') {
        gatewayDetailsHook.openDetailsInViewingMode(gatewayToOpen);
      } else if (gatewayDetailAction === 'editing') {
        gatewayDetailsHook.openDetailsInEditingMode(gatewayToOpen);
      } else {
        gatewayDetailsHook.changeCurrentSelectedGateway(gatewayToOpen);
      }

      setGatewayIdToOpen(undefined);
      setGatewayDetailAction(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayToOpen, gatewayToOpenIsFetching]);

  useEffect(() => {
    if (gatewayToOpenIsError && gatewayToOpenError) {
      setErrorMessage(gatewayToOpenError);
      setGatewayIdToOpen(undefined);
      setGatewayDetailAction(undefined);
    }
  }, [gatewayToOpenError, gatewayToOpenIsError, setErrorMessage]);

  const openDetailsInCreationMode = () => {
    gatewayDetailsHook.openDetailsInCreationMode();
  };

  const openDetailsInEditingModeAndRefresh = (gateway: GatewayOverviewRecord) => {
    setGatewayDetailAction('editing');
    setGatewayIdToOpen(gateway.id);
  };

  const openDetailsInViewingModeAndRefresh = (gateway: GatewayOverviewRecord) => {
    setGatewayDetailAction('viewing');
    setGatewayIdToOpen(gateway.id);
  };

  const deleteGateway = (gateway: GatewayOverviewRecord) => {
    gatewayDetailsHook.deleteGateway(gateway);
  };

  const setGatewayDetailsToOpen = (id: string): void => {
    setGatewayIdToOpen(id);
    setGatewayDetailAction('viewing');
  };

  return {
    gatewayDetailsHook,
    gatewayFilterHook,

    openDetailsInCreationMode,
    openDetailsInViewingModeAndRefresh,
    openDetailsInEditingModeAndRefresh,
    deleteGateway,
    refresh,
  };
};

export default useGateways;
